import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import {
  enableNotifications,
  isPushSubscribed,
  getNotificationPermission,
  isNotificationSupported,
} from '../lib/oneSignalService';

/**
 * Job alerts on/off switch for seekers. Controls the `job_alerts` notification
 * preference (default ON, opt-out) that the backend new-job fan-out honours
 * (notify_seekers_of_new_job / announce_strong_job_matches). Turning it ON is
 * also the moment we ask for push permission, so the alert can actually be
 * delivered instead of only landing as an in-app row the seeker has to open.
 */
const JobAlertsSetting = ({ profile }) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await api.get('/api/profile/notification-preferences', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        // Default ON: only an explicit false counts as off.
        if (active) setEnabled(res.data?.preferences?.job_alerts !== false);
      } catch (err) {
        console.error('Failed to load job-alerts preference:', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Best-effort push opt-in when the seeker turns alerts on — never blocks the
  // toggle (the OneSignal prompt/optIn can take seconds, and the preference is
  // already saved by the time this runs).
  const askForPushPermission = async () => {
    try {
      if (!isNotificationSupported() || getNotificationPermission() === 'denied') return;
      const subscribed = await isPushSubscribed();
      if (subscribed) return;
      const result = await enableNotifications();
      if (result?.success) {
        toast.success(t('jobAlerts.pushEnabled', 'Push on — you’ll hear about new matching jobs.'));
      }
    } catch {
      /* permission ask is best-effort; the preference is what the backend honours */
    }
  };

  const handleToggle = async (checked) => {
    const prev = enabled;
    setEnabled(checked);
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('no session');
      await api.put('/api/profile/notification-preferences', { job_alerts: checked }, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (checked) askForPushPermission(); // fire-and-forget
    } catch (err) {
      setEnabled(prev);
      toast.error(t('jobAlerts.updateFailed', 'Could not update job alerts. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell size={20} />
          {t('jobAlerts.title', 'Job alerts')}
        </CardTitle>
        <CardDescription>
          {t('jobAlerts.description', 'Get notified when a new job matching your skills and location is posted.')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <Label htmlFor="job_alerts" className="flex-1 cursor-pointer pr-4">
            <div className="font-medium text-sm">{t('jobAlerts.toggleLabel', 'New job alerts')}</div>
            <div className="text-xs text-muted-foreground">
              {t('jobAlerts.toggleDesc', 'Turn on to hear about new matching jobs first.')}
            </div>
          </Label>
          <Switch
            id="job_alerts"
            checked={enabled}
            disabled={saving}
            onCheckedChange={handleToggle}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default JobAlertsSetting;
